<template>
  <div class="rounded-lg bg-white border border-slate-300 overflow-x-auto w-full max-w-full" :class="error ? 'border-red-500' : 'border-slate-300'">
    <div class="overflow-x-auto w-full max-w-full relative">
      <table class="w-full text-black">
        <thead class="uppercase sticky top-0">
          <tr v-if="headers" class="border-b border-slate-300 bg-slate-200">
            <th
              v-for="(header, index) in headers"
              :key="index"
              draggable="true"
              @dragstart="dragStart(index)"
              @dragover.prevent
              @drop="drop(index)"
              class="cursor-move text-nowrap"
              :class="[
                {
                  'min-w-[80px]': header.title === 'Ações' && !header.classes,
                  'divide-x border-gray-500': cellBorder,
                },
                cellSpacing,
              ]"
              :style="header.width ? `width: ${header.width}` : ''">
              <Flex gap-3 items-center :justify-center="header.title === 'Ações'" :class="[header.classes]">
                {{ header.title }}
                <i
                  :class="[handleIconsClass(header), 'cursor-pointer text-[0.8em]']"
                  v-if="header.title != 'Ações' && !header.disableOrder && !disableOrdenation"
                  @click="handleHeaderClick(header)"></i>
              </Flex>
            </th>
            <th v-if="expanded" :class="[cellSpacing]"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index) in processedItems" :key="index">
            <tr
              :class="[
                { 'border-b border-slate-300 last:border-none': !expanded || !item.expanded },
                { 'bg-blue-500 text-white': compareItem(item) },
                { 'hover:bg-blue-100': !compareItem(item) },
                { 'text-red-400': item.errorLine },
                { 'divide-x ': cellBorder },
                computedClass(item),
              ]"
              @click.right.prevent="sendRightClick(item)"
              @click="sendLeftClick(item)"
              @dblclick="sendDoubleClick(item)">
              <td v-for="header in headers" :key="header" :class="[header.align, { 'text-nowrap': noWrap }, cellSpacing]" :style="header.width ? `width: ${header.width}` : ''">
                <div v-if="item" class="h-full" :class="[header.title === 'Ações' ? 'flex justify-center' : '']">
                  <slot :name="header.field" :item="item" :rowIndex="index">
                    {{ getItemValue(item, header) }}
                    <!-- {{ item[header.field] }} -->
                  </slot>
                </div>
              </td>

              <td v-if="expanded && mapNotExpandedItems(item)" class="w-4">
                <IconSolid :icon="item.expanded ? 'fa-sort-down' : 'fa-sort-up'" @click="item.expanded = !item.expanded" />
              </td>
            </tr>

            <TransitionX>
              <tr v-if="expanded && item.expanded" class="border-b border-slate-300">
                <td :colspan="headers?.length + 1">
                  <slot name="expanded" :item="item"></slot>
                </td>
              </tr>
            </TransitionX>
          </template>

          <tr v-if="sumHeaders?.length > 0">
            <td :class="cellSpacing" v-for="header in headers" :key="header.field">
              <span class="font-bold" v-if="sumHeaders.some((item) => item.field === header.field)">
                {{
                  header.format === 'money' ? formatMoneyS(getColumnSum(header.field)) : header.format === 'float' ? getColumnSum(header.field).toString().replace('.', ',') : ''
                }}
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td v-if="total && items?.length" class="border-b border-slate-300 w-full" :colspan="headers?.length">
              <div class="flex justify-end mx-6 my-3">
                <div :key="index" class="flex gap-3">
                  <p class="font-bold">QUANTIDADE TOTAL</p>
                  <p>{{ totalValue }}</p>
                </div>
              </div>
            </td>
            <slot v-else name="footer" />
          </tr>
        </tfoot>
      </table>
    </div>
    <p class="text-center italic py-5" v-if="items && items?.length < 1">Nenhum item a ser exibido.</p>
    <div :class="[cellSpacing]" v-if="!disablePagination">
      <NavigationBar
        v-if="items?.length"
        v-model="currentPage"
        :disablePagination="disablePagination"
        :itemsPerPage="items?.length"
        :limit="itemsPerPage"
        :totalItems="totalItems"
        :totalPages="totalPages" />
    </div>
  </div>

  <div class="text-red-400 text-xs mt-3 flex justify-end" v-if="error">
    <b>{{ error }}</b>
  </div>
</template>

<script setup>
import { useFormat } from '@/composables/useFormat'
const { formatMoneyS } = useFormat()
import { ref, onMounted, watch, computed, onBeforeUnmount } from 'vue'

const emit = defineEmits(['selectedItem', 'update:modelValue', 'order', 'rightClick', 'doubleClick'])

const props = defineProps({
  disableOrdenation: {
    type: Boolean,
    default: false,
  },
  disablePagination: Boolean,
  rowClicked: Number,
  error: Boolean,
  total: Boolean,
  totalValue: {
    type: Number,
    default: 0,
  },
  sumHeaders: {
    type: Array,
    default: [],
  },
  headers: {
    type: Array,
    default: [],
    required: true,
  },
  items: Array,
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  modelValue: {
    type: Number,
    default: 1,
  },
  totalItems: Number,
  totalPages: Number,
  expanded: Boolean,
  notExpandFieldsWith: Array,
  startsExpanded: Boolean,
  dense: Boolean,
  ultraDense: Boolean,
  cellBorder: Boolean,
  zoom: Boolean,
  loader: Boolean,
  noWrap: Boolean,
  disableSelection: {
    type: Boolean,
    default: false,
  },
})

const currentPage = ref(1)
const orderField = ref({ table: '', column: '', direction: '' })
const dragIndex = ref(null)
const selectedItem = ref(null)

const dragStart = (index) => {
  dragIndex.value = index
}

const drop = (dropIndex) => {
  if (dragIndex.value === null || dragIndex.value === dropIndex) return

  const draggedHeader = props.headers[dragIndex.value]
  props.headers.splice(dragIndex.value, 1)
  props.headers.splice(dropIndex, 0, draggedHeader)
  dragIndex.value = null
}

const getColumnSum = (field) => {
  const totalSum = props.items.reduce((acc, item) => {
    const value = parseFloat(item[field])
    if (!isNaN(value)) {
      acc += value
    }
    return acc
  }, 0)

  return totalSum
}

const mapNotExpandedItems = (item) => {
  if (!props.notExpandFieldsWith || props.notExpandFieldsWith?.length === 0) {
    return true
  }

  const conditions = props.notExpandFieldsWith.map((field) => {
    return item[field.field] !== field.value
  })

  return conditions.every(Boolean)
}

const getItemValue = (item, header) => {
  const rawValue = item[header.field]
  if (header.format && typeof header.format == 'function') {
    const formattedValue = header.format(rawValue)
    return formattedValue
  }
  return rawValue
}

const handleHeaderClick = (header) => {
  header.orderState = (header.orderState % 3) + 1

  switch (header.orderState) {
    case 1:
      header.direction = null
      break
    case 2:
      header.direction = 'asc'
      break
    case 3:
      header.direction = 'desc'
      break
  }
  let headerField = header.orderField ? header.orderField : header.field
  let tableField = header.tableOrder ?? orderField.value.table
  filter(headerField, header.direction, tableField)
}

const handleIconsClass = (header) => {
  let string = ''

  if (header.direction === 'asc' && orderField.value.column === header.field && orderField.value.table === header.tableOrder && header.orderState === 2) {
    string += ' fa-solid fa-sort-up '
  } else if (header.direction === 'desc' && orderField.value.column === header.field && header.orderState === 3) {
    string += ' fa-solid fa-sort-down '
  } else {
    string += ' fa-solid fa-sort'
  }

  return string
}

const filter = (column, direction, table) => {
  emit('order', { direction, column, table })
  orderField.value.column = column
  orderField.value.table = table
}

const sendRightClick = (item) => {
  emit('rightClick', item)
}

const sendDoubleClick = (item) => {
  emit('doubleClick', item)
}

const sendLeftClick = (item) => {
  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const compareItem = (item) => {
  if (props.disableSelection) return false
  return JSON.stringify(item) == selectedItem.value
}

const computedClass = (item) => {
  return item.class
}

const cellSpacing = computed(() => {
  if (props.ultraDense) {
    return 'py-[2px] px-[5px] text-[0.8em]'
  }
  if (props.dense) {
    return 'px-2 py-1 text-sm'
  }
  return 'px-5 py-2'
})

const processedItems = computed(() => {
  if (props.startsExpanded) {
    return props.items.map((item) => ({
      ...item,
      expanded: true,
    }))
  }
  return props.items
})

/* =========================================================
 * ✅ ADICIONADO: navegação por teclado (ArrowUp / ArrowDown)
 * ========================================================= */
const getSelectedIndex = () => {
  if (!selectedItem.value) return -1
  const list = processedItems.value || []
  return list.findIndex((it) => JSON.stringify(it) === selectedItem.value)
}

const selectByIndex = (index) => {
  const list = processedItems.value || []
  if (!list.length) return

  const bounded = Math.max(0, Math.min(index, list.length - 1))
  const item = list[bounded]

  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const moveSelection = (delta) => {
  const list = processedItems.value || []
  if (!list.length) return

  const currentIdx = getSelectedIndex()

  // se ainda não tem seleção, começa pela primeira linha (ou última se subir)
  if (currentIdx === -1) {
    selectByIndex(delta > 0 ? 0 : list.length - 1)
    return
  }

  selectByIndex(currentIdx + delta)
}

const onKeyDown = (e) => {
  // não roubar teclado de input/textarea/editor
  const tag = e.target?.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveSelection(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveSelection(-1)
  }
}
/* ========================= end adicionado ========================= */

watch(
  () => props.modelValue,
  (newVal) => {
    currentPage.value = newVal
  },
)

watch(currentPage, (page) => {
  emit('update:modelValue', page)
})

watch(
  () => props.headers,
  (newHeaders, oldHeaders) => {
    if (newHeaders && oldHeaders) {
      const oldHeadersMap = oldHeaders.reduce((acc, header) => {
        acc[header.field] = header.orderState
        return acc
      }, {})

      const sumHeadersMap = props.sumHeaders.reduce((acc, sumHeader) => {
        acc[sumHeader.field] = sumHeader.format || null
        return acc
      }, {})

      newHeaders.forEach((header) => {
        const key = header.field
        header.orderState = oldHeadersMap[key] !== undefined ? oldHeadersMap[key] : 1

        if (sumHeadersMap[key]) {
          header.format = sumHeadersMap[key]
        }
      })
    }
  },
  { deep: true },
)

onMounted(() => {
  if (props.headers) {
    props.headers.forEach((header) => (header.orderState = 1))

    props.headers.map((header) => {
      props.sumHeaders.forEach((sumHeader) => {
        if (sumHeader.field === header.field && sumHeader.format) {
          header.format = sumHeader.format
        }
      })
    })
  }
  currentPage.value = props.modelValue

  // ✅ ADICIONADO: listener global
  window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  // ✅ ADICIONADO: cleanup
  window.removeEventListener('keydown', onKeyDown)
})
</script>
